import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './RecipeCard.module.css';
import { RecipeData } from '../../context/RecipeContext';
import RecipeTypeIndicator from '../RecipeTypeIndicator';
import RecipeDietaryAttributes from '../RecipeDietaryAttributes/RecipeDietaryAttributes';

// Helper function to format date
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) {
    return 'Recently';
  }
  
  const date = new Date(dateString);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return 'Recently';
  }
  
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) {
    return 'Today';
  } else if (diffDays === 2) {
    return 'Yesterday';
  } else if (diffDays <= 7) {
    return `${diffDays - 1} days ago`;
  } else if (diffDays <= 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
};

interface RecipeCardProps {
  recipe: RecipeData;
  isSaved?: boolean;
  onViewClick: (recipe: RecipeData) => void;
  onSaveClick?: (recipeId: string) => void;
  onUnsaveClick?: (recipeId: string) => void;
  onCustomizeClick?: (recipe: RecipeData) => void;
  onDeleteClick?: (recipeId: string) => void;
  showSaveButton?: boolean;
  showUnsaveButton?: boolean;
  showCustomizeButton?: boolean;
  showDeleteButton?: boolean;
  variant?: 'default' | 'compact';
  dietaryInfo?: { vegan?: boolean; [key: string]: any };
  userPreferences?: any;
  matchingDietaryTags?: Array<{ key: string; label: string }>;
}

const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  isSaved = false,
  onViewClick,
  onSaveClick,
  onUnsaveClick,
  onCustomizeClick,
  onDeleteClick,
  showSaveButton = true,
  showUnsaveButton = false,
  showCustomizeButton = true,
  showDeleteButton = false,
  variant = 'default',
  dietaryInfo,
  userPreferences,
  matchingDietaryTags = []
}) => {
  const navigate = useNavigate();
  
  const handleSaveClick = () => {
    if (onSaveClick) {
      onSaveClick(recipe.id);
    }
  };

  const handleUnsaveClick = () => {
    if (onUnsaveClick) {
      onUnsaveClick(recipe.id);
    }
  };

  const handleCustomizeClick = () => {
    if (onCustomizeClick) {
      onCustomizeClick(recipe);
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteClick) {
      onDeleteClick(recipe.id);
    }
  };

  const handleViewClick = () => {
    // Navigate to the correct URL based on recipe type
    if (recipe.type === 'variant') {
      navigate(`/variants/${recipe.id}/view`);
    } else {
      navigate(`/recipes/${recipe.id}/view`);
    }
  };

  return (
    <div className={`${styles.recipeCard} ${styles[variant]}`}>
      {/* Image */}
      {(() => {
        const primary = recipe.photos?.find(p => p.is_primary) || recipe.photos?.[0]
        const imgSrc = primary?.url || recipe.image || null
        return (
          <div className={styles.recipeImage}>
            {imgSrc ? (
              <img src={imgSrc} alt={recipe.title} />
            ) : (
              <div className={styles.placeholder}>
                <div className={styles.placeholderIcon}>🍽️</div>
                <div className={styles.placeholderText}>No image yet</div>
              </div>
            )}
          </div>
        )
      })()}
      <div className={styles.recipeContent}>
        <div className={styles.recipeTitleRow}>
          <h3 className={styles.recipeTitle}>{recipe.title}</h3>
        </div>
        
        {/* Recipe dietary attributes */}
        {recipe.dietary_info && (
          <div className={styles.dietaryTagsRow}>
            <RecipeDietaryAttributes
              dietaryAttributes={recipe.dietary_info}
              userPreferences={userPreferences}
              variant="compact"
              showOnlyMatches={true}
              maxTags={3}
            />
          </div>
        )}
        
        <div className={styles.recipeIndicators}>
          {recipe.type && (
            <RecipeTypeIndicator type={recipe.type} />
          )}
        </div>
        
        <div className={styles.recipeInfo}>
          <div className={styles.ingredientsPreview}>
            <strong>Ingredients:</strong>
            <ul>
              {recipe.ingredients.slice(0, 3).map((ingredient, index) => (
                <li key={index}>
                  {ingredient.quantity} {ingredient.unit} {ingredient.name}
                </li>
              ))}
              {recipe.ingredients.length > 3 && (
                <li>... and {recipe.ingredients.length - 3} more</li>
              )}
            </ul>
          </div>
          
          <div className={styles.recipeStats}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Steps:</span>
              <span className={styles.statValue}>{recipe.instructions.length}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Added:</span>
              <span className={styles.statValue}>{formatDate(recipe.created_at)}</span>
            </div>
          </div>
        </div>
        
        <div className={styles.recipeActions}>
          <button className={styles.viewButton} onClick={handleViewClick}>View Recipe</button>
          
          {showCustomizeButton && onCustomizeClick && (
            <button
              className={styles.customizeButton}
              onClick={handleCustomizeClick}
              title={recipe.type === 'variant' ? "Edit this variant recipe" : "Create your own version of this recipe"}
            >
              {recipe.type === 'variant' ? 'Edit' : 'Customize'}
            </button>
          )}
          
          {showSaveButton && onSaveClick && !isSaved && (
            <button
              className={styles.saveButton}
              onClick={handleSaveClick}
            >
              Save
            </button>
          )}
          
          {showUnsaveButton && onUnsaveClick && isSaved && (
            <button
              className={styles.unsaveButton}
              onClick={handleUnsaveClick}
              title="Remove from saved"
            >
              Unsave
            </button>
          )}
          
          {showDeleteButton && onDeleteClick && (recipe.type === 'created' || recipe.type === 'variant') && (
            <button
              className={styles.deleteButton}
              onClick={handleDeleteClick}
              title="Delete this recipe"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;